import type { ConversionPhase, ConvertedProject } from './convertLegacyProject';
import type { ConversionResponse } from './protocol';

export interface ConversionWorker {
  onmessage: ((event: MessageEvent<ConversionResponse>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  onmessageerror: ((event: MessageEvent) => void) | null;
  postMessage(value: unknown): void;
  terminate(): void;
}

/** Each attempt has a fresh worker. Cancellation releases parser/codec data and
 * stale messages cannot publish another attempt's download or storage result. */
export class ConversionWorkerClient {
  private sequence = 0;
  private active?: { worker: ConversionWorker; cancel: () => void };
  constructor(
    private readonly createWorker: () => ConversionWorker = () =>
      new Worker(new URL('./conversion.worker.ts', import.meta.url), { type: 'module' }),
  ) {}

  convert(
    raw: unknown,
    signal: AbortSignal,
    onPhase: (phase: ConversionPhase) => void,
    destinationName?: string,
    onOriginal?: (blob: Blob) => void,
    kind?: 'chip',
  ): Promise<ConvertedProject> {
    this.cancel();
    if (signal.aborted)
      return Promise.reject(new DOMException('Conversion cancelled.', 'AbortError'));
    return new Promise((resolve, reject) => {
      const worker = this.createWorker(),
        id = ++this.sequence;
      const finish = (error?: Error, project?: ConvertedProject) => {
        if (this.active?.worker !== worker) return;
        this.active = undefined;
        signal.removeEventListener('abort', cancel);
        worker.onmessage = worker.onerror = worker.onmessageerror = null;
        worker.terminate();
        if (error) reject(error);
        else resolve(project!);
      };
      const cancel = () =>
        finish(new DOMException('Conversion cancelled.', 'AbortError'));
      this.active = { worker, cancel };
      signal.addEventListener('abort', cancel, { once: true });
      worker.onmessage = ({ data }) => {
        if (data.id !== id || this.active?.worker !== worker) return;
        if (data.kind === 'progress') onPhase(data.phase);
        else if (data.kind === 'original') onOriginal?.(data.blob);
        else if (data.kind === 'failed') finish(new Error(data.error));
        else finish(undefined, data.project);
      };
      worker.onerror = () =>
        finish(
          new Error(
            'The upgrade worker stopped. Try opening the file again; its original is preserved.',
          ),
        );
      worker.onmessageerror = () =>
        finish(new Error('The conversion result could not be read.'));
      if (signal.aborted) {
        cancel();
        return;
      }
      try {
        worker.postMessage({
          id,
          raw,
          destinationName,
          includeOriginal: !!onOriginal,
          ...(kind ? { kind } : {}),
        });
      } catch (error) {
        finish(
          error instanceof Error
            ? error
            : new Error('Unable to send the project for conversion.'),
        );
      }
    });
  }

  cancel(): void {
    this.active?.cancel();
  }
}
